/**
 * Program QA Service
 * Quality assurance reviews, defects, and metrics
 */
import { apiClient } from './api'

export interface QAReview {
  id: number
  program_id: number
  project_id?: number
  review_type: string
  reviewer_id?: number
  outcome: string
  notes?: string
  review_date: string
  created_at: string
}

export interface Defect {
  id: number
  program_id: number
  project_id?: number
  defect_id: string
  description: string
  severity: string
  status: string
  reported_by_id?: number
  assigned_to_id?: number
  reported_date: string
  resolved_date?: string
  metadata?: Record<string, unknown>
}

export interface QAMetrics {
  id: number
  program_id: number
  test_coverage?: number
  defect_density?: number
  test_execution?: number
  automation_ratio?: number
  period_start?: string
  period_end?: string
  calculated_at: string
  metadata?: Record<string, unknown>
}

export const programQAService = {
  /**
   * List QA reviews
   */
  async listReviews(
    programId: number,
    reviewType?: string,
    outcome?: string
  ): Promise<QAReview[]> {
    const params: Record<string, unknown> = {}
    if (reviewType) params.review_type = reviewType
    if (outcome) params.outcome = outcome
    
    const response = await apiClient.get(
      `/api/v1/programs/${programId}/qa/reviews`,
      { params }
    )
    return response.data
  },

  /**
   * Create QA review
   */
  async createReview(programId: number, data: Partial<QAReview>): Promise<QAReview> {
    const response = await apiClient.post(
      `/api/v1/programs/${programId}/qa/reviews`,
      data
    )
    return response.data
  },

  /**
   * List defects
   */
  async listDefects(
    programId: number,
    severity?: string,
    status?: string
  ): Promise<Defect[]> {
    const params: Record<string, unknown> = {}
    if (severity) params.severity = severity
    if (status) params.status = status
    
    const response = await apiClient.get(
      `/api/v1/programs/${programId}/qa/defects`,
      { params }
    )
    return response.data
  },

  /**
   * Create defect
   */
  async createDefect(programId: number, data: Partial<Defect>): Promise<Defect> {
    const response = await apiClient.post(
      `/api/v1/programs/${programId}/qa/defects`,
      data
    )
    return response.data
  },

  /**
   * Update defect
   */
  async updateDefect(
    programId: number,
    defectId: number,
    data: Partial<Defect>
  ): Promise<Defect> {
    const response = await apiClient.put(
      `/api/v1/programs/${programId}/qa/defects/${defectId}`,
      data
    )
    return response.data
  },

  /**
   * Get QA metrics
   */
  async getMetrics(programId: number): Promise<QAMetrics> {
    const response = await apiClient.get(
      `/api/v1/programs/${programId}/qa/metrics`
    )
    return response.data
  },

  /**
   * Create or update QA metrics
   */
  async createMetrics(programId: number, data: Partial<QAMetrics>): Promise<QAMetrics> {
    const response = await apiClient.post(
      `/api/v1/programs/${programId}/qa/metrics`,
      data
    )
    return response.data
  },
}

